import {useNavigate} from "react-router-dom";

import Page from "@/components/Page";
import {
	DsEmpty,
	DsHeader,
	DsKpi,
	DsKpiRow,
	DsLink,
	DsMissingSources,
	DsSection,
	DsStatus,
	DsTable,
	DsSkeleton,
} from "@/components/DsKit";

import {useClubSnapshot} from "@/utils/hooks";
import {
	playerName,
	wellnessCoverage,
	summarizeClubWellness,
} from "@/utils/clubSnapshot";

const fmt = (v) =>
	v === null || v === undefined
		? "—"
		: v.toLocaleString("it-IT", {
				minimumFractionDigits: 1,
				maximumFractionDigits: 1,
			});

const tqrStatus = (v) =>
	v === null ? "none" : v >= 15 ? "ok" : v >= 13 ? "warn" : "alert";

const rpeStatus = (v) =>
	v === null ? "none" : v <= 5 ? "ok" : v <= 7 ? "warn" : "alert";

function DsPerformancePage(props) {
	const {handleNotification} = props;
	const navigate = useNavigate();
	const {
		data: s,
		loading,
		reload,
		scope,
	} = useClubSnapshot({handleNotification});

	const teams = s?.teams || [];
	const all = teams.flatMap((t) =>
		t.wellness.players.map((w) => ({...w, team: t})),
	);
	const selectedWellness = summarizeClubWellness(teams);

	const injuredIds = new Set((s?.activeInjuries || []).map((i) => i.playerId));
	const watchList = all
		.filter(
			(w) =>
				!injuredIds.has(w.player.id) &&
				((w.avgTQR !== null && w.avgTQR < 13) ||
					(w.avgRPE !== null && w.avgRPE >= 8)),
		)
		.sort((a, b) => (a.avgTQR ?? 99) - (b.avgTQR ?? 99));

	const columns = [
		{key: "name", label: "Squadra", render: (t) => <b>{t.name}</b>},
		{
			key: "tqr",
			label: "Recupero",
			align: "center",
			render: (t) => (
				<DsStatus
					status={tqrStatus(t.wellness.avgTQR)}
					label={fmt(t.wellness.avgTQR)}
				/>
			),
		},
		{
			key: "rpe",
			label: "Fatica",
			align: "center",
			render: (t) => (
				<DsStatus
					status={rpeStatus(t.wellness.avgRPE)}
					label={fmt(t.wellness.avgRPE)}
				/>
			),
		},
		{
			key: "tqrCoverage",
			label: "Copertura TQR",
			render: (t) => wellnessCoverage(t.wellness.tqr, t.wellness.expected),
		},
		{
			key: "rpeCoverage",
			label: "Copertura RPE",
			render: (t) => wellnessCoverage(t.wellness.rpe, t.wellness.expected),
		},
		{
			key: "trainings",
			label: "Allenamenti 7gg",
			align: "center",
			render: (t) => t.trainingsWeek,
		},
		{
			key: "alerts",
			label: "Segnalazioni DS",
			align: "center",
			render: (t) => {
				const n = t.wellness.lowTQR.length + t.wellness.highRPE.length;
				return n ? <DsStatus status="warn" label={n} /> : "0";
			},
		},
	];

	return (
		<Page>
			<div className="ds">
				<DsHeader
					title="Performance"
					subtitle="Ultimi 7 giorni inclusivi fino a oggi · media dei giocatori unici con dati"
					snapshot={s}
					scope={scope}
					area="performance"
					onReload={reload}
					reloading={loading}
				/>
				<DsSkeleton visible={loading && !s} />
				{s && (
					<>
						<DsMissingSources sources={s.missingSources} />
						<DsKpiRow>
							<DsKpi
								label="Recupero · TQR"
								value={
									selectedWellness ? fmt(selectedWellness.avgTQR) : "Per rosa"
								}
								sub={
									selectedWellness
										? "su 20 · " +
											wellnessCoverage(
												selectedWellness.tqr,
												selectedWellness.expected,
											)
										: "Seleziona una rosa; medie separate nella tabella"
								}
							/>
							<DsKpi
								label="Sforzo · RPE"
								value={
									selectedWellness ? fmt(selectedWellness.avgRPE) : "Per rosa"
								}
								sub={
									selectedWellness
										? "su 10 · " +
											wellnessCoverage(
												selectedWellness.rpe,
												selectedWellness.expected,
											)
										: "Seleziona una rosa; medie separate nella tabella"
								}
							/>
							<DsKpi
								label="Segnalazioni DS"
								value={watchList.length}
								sub="Euristiche sperimentali; non un giudizio di forma"
							/>
						</DsKpiRow>

						<DsSection
							title="Condizione per squadra"
							hint="Giocatori appartenenti alla rosa: misure autorizzate anche da altre sedute o gare. Sintesi club su persone uniche, riepiloghi per rosa conservati; coperture TQR/RPE distinte."
							action={<DsLink to="/measurations/wellness">Benessere</DsLink>}
						>
							<DsTable columns={columns} rows={teams} />
						</DsSection>

						<DsSection
							title="Segnalazioni DS sperimentali"
							hint="Euristiche DS già esistenti: TQR sotto13 o RPE da8. Esclusi gli infortunati; non sono soglie della Panoramica né un giudizio clinico."
						>
							{watchList.length ? (
								<div className="ds-list">
									{watchList.map((w) => (
										<div
											key={w.player.id + w.team.id}
											className="ds-list__item is-clickable"
											onClick={() => navigate("/ds/giocatore/" + w.player.id)}
										>
											<div className="ds-list__main">
												<div className="ds-list__title">
													{playerName(w.player)}
												</div>
												<div className="ds-list__sub">
													{w.team.name}
													{w.player.primaryRole
														? " · " + w.player.primaryRole
														: ""}
												</div>
											</div>
											<DsStatus
												status={tqrStatus(w.avgTQR)}
												label={"Recupero " + fmt(w.avgTQR)}
											/>
											<DsStatus
												status={rpeStatus(w.avgRPE)}
												label={"Fatica " + fmt(w.avgRPE)}
											/>
										</div>
									))}
								</div>
							) : (
								<DsEmpty>
									Nessuna segnalazione DS dai dati disponibili; l’assenza di
									misure non certifica la condizione.
								</DsEmpty>
							)}
						</DsSection>
					</>
				)}
			</div>
		</Page>
	);
}

export default DsPerformancePage;
